import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  governedSlugOf,
  parseJsonArrayColumn,
  type ModuleCatalogRecord,
  type ModuleKindFacet,
  type ModuleSummaryRecord,
} from "../modules.types";
import { subscribeModuleEvents, type ModuleServiceEvent } from "../modulesRealtime";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * A Feathers query object (flattened bracket keys allowed). Local stand-in for
 * the removed transport helper's `ApiQueryParams` — same shape ClientsStore
 * declares.
 */
type ApiQueryParams = {
  $limit?: number;
  $skip?: number;
  $sort?: Record<string, number>;
  [key: string]: unknown;
};

/** A Feathers paginated `find` envelope. */
interface PaginatedResult<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}

/**
 * Normalise a `find` result into a plain array of rows: a paginated
 * KnexService returns `{ data: [...] }`; a custom/derived service returns the
 * bare array.
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/** Status facet — "all" plus whatever `module_catalog.status` values exist. */
export type ModuleStatusFilter = "all" | string;

/**
 * Sortable columns. Half of them (`clients`, `owners`, `governance`,
 * `activity`) come from the joined `module-summary` rollup and could never ride
 * a `module-catalog` `$sort` — which is one of the two reasons this list sorts
 * CLIENT-side (see the class docblock).
 */
export type ModuleSortKey =
  | "name"
  | "key"
  | "kind"
  | "status"
  | "clients"
  | "owners"
  | "governance"
  | "activity";

/** Counts and dates read best biggest/newest-first; text columns read A→Z. */
const MODULES_DESC_FIRST: ReadonlySet<ModuleSortKey> = new Set<ModuleSortKey>([
  "clients",
  "owners",
  "governance",
  "activity",
]);

/**
 * Guard rail on the catalog fetch. The governed catalog is bounded by the
 * Coordinator registry (21 entries as of 2026-08-11) and is not expected to
 * reach three digits, but the store reports honestly when the API has more rows
 * than it fetched rather than silently showing a partial catalog.
 */
export const MODULES_FETCH_LIMIT = 500;

/** Facet counts for the summary cards. */
interface ModuleKindCounts {
  all: number;
  module: number;
  tool: number;
  integration: number;
  /** `core` + `library` — listed, never assignable. */
  platform: number;
}

/**
 * View store for the Modules (catalog) list.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. `module-catalog` is an EXISTING registered ServiceStore
 * (serviceStores/clients.stores.ts) — this area deliberately does not redeclare
 * it; `module-summary` is registered by serviceStores/modules.stores.ts.
 *
 * ONE PAGE, THREE KINDS: the list is the catalog — one row per `module_catalog`
 * entry — joined to `module-summary` for the adoption / owner / governance
 * columns. Modules, tools and integrations are served near-identically; kind is
 * a header FACET, not a separate page.
 *
 * LIST vs RESOLVE (Coordinator CATALOG.md §3 — the safety rule): this is a LIST
 * surface, so it shows EVERY row, including `assignable = 0` platform entries
 * (`core`, `libraries`, `payroll`) and `componentOf` components. They are
 * marked with a chip rather than hidden — hiding them is what makes a mirrored
 * HQ quietly wrong. Only RESOLVE surfaces (the attribution resolver feed, the
 * LLM governed roster, the client-module picker) filter `assignable = 1`.
 *
 * Querying is CLIENT-side, unlike ClientsStore. Two reasons, both structural:
 * (1) the catalog is bounded and small — the whole thing arrives in one find,
 * so facet counts stay exact and filtering is instant; (2) four of the eight
 * sortable columns are rollup values from a different service and can never
 * ride the catalog's `$sort`. Sorting still goes through the shared
 * `components/TableSort` helpers so the semantics match every other list.
 *
 * DOCUMENTED FALLBACK: the `module-summary` rollup degrades independently —
 * a failed fetch clears `summaryAvailable`, the joined columns render "-", and
 * the catalog identity columns keep working. The catalog is what the page is
 * for; the rollup is decoration.
 */
class ModulesStore {
  rootStore: RootStore;

  /** Every catalog entry (one find; no paging — see the class docblock). */
  catalog: ModuleCatalogRecord[] = [];
  /** Server-reported total, so a truncated fetch can be reported honestly. */
  total = 0;
  loading = false;
  error: string | null = null;

  /** Per-entry rollup from `module-summary`, keyed by governedSlug. */
  summaryBySlug = new Map<string, ModuleSummaryRecord>();
  summaryAvailable = false;

  // Header filters
  searchQuery = "";
  kindFilter: ModuleKindFacet = "all";
  statusFilter: ModuleStatusFilter = "all";
  /** Show only entries a client may actually be assigned (`assignable = 1`). */
  assignableOnly = false;

  // Column sort — CLIENT-side over the filtered rows.
  sortKey: ModuleSortKey = "name";
  sortDir: SortDir = "asc";

  private unsubscribeLive: (() => void) | null = null;
  /** Monotonic guard so a stale find can never clobber a newer load. */
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The EXISTING `module-catalog` ServiceStore (clients.stores.ts). */
  private get catalogService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ??
        this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The `module-summary` rollup ServiceStore (modules.stores.ts). */
  private get summaryService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-summary") ??
        this.rootStore.moduleSummaryStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  // ---- Lifecycle ----------------------------------------------------------

  /**
   * Route entry point (idempotent — the header Refresh button re-runs
   * `onEnter`). Loads the catalog + rollup and ensures the realtime
   * subscription is live.
   */
  async initialize() {
    if (!this.unsubscribeLive) {
      // Shared realtime bus (src/realtime): the JWT is the bus's business.
      this.unsubscribeLive = subscribeModuleEvents(
        (event) => this.handleLiveEvent(event),
        this.rootStore,
      );
    }
    await this.reload();
  }

  /** Route exit: tear down the realtime subscription (refcounted socket). */
  dispose() {
    this.unsubscribeLive?.();
    this.unsubscribeLive = null;
  }

  // ---- Derived data -------------------------------------------------------

  summaryFor(governedSlug: string): ModuleSummaryRecord | undefined {
    return this.summaryBySlug.get(governedSlug);
  }

  /** Rollup for a catalog row (`moduleKey` IS the governed slug). */
  summaryOf(record: ModuleCatalogRecord): ModuleSummaryRecord | undefined {
    return this.summaryFor(governedSlugOf(record));
  }

  /** True when the API reported more catalog rows than this store fetched. */
  get truncated(): boolean {
    return this.total > this.catalog.length;
  }

  /** Facet counts over the WHOLE catalog (independent of the active filters). */
  get kindCounts(): ModuleKindCounts {
    const counts: ModuleKindCounts = {
      all: this.catalog.length,
      module: 0,
      tool: 0,
      integration: 0,
      platform: 0,
    };
    for (const row of this.catalog) {
      if (row.kind === "module") counts.module += 1;
      else if (row.kind === "tool") counts.tool += 1;
      else if (row.kind === "integration") counts.integration += 1;
      else counts.platform += 1;
    }
    return counts;
  }

  /** Entries a client may be assigned — the RESOLVE-eligible subset. */
  get assignableCount(): number {
    // `!== false`, never truthy: an ABSENT field is a pre-migration API, where
    // every row is assignable by construction. See the field's docblock.
    return this.catalog.filter((row) => row.assignable !== false).length;
  }

  /** Listed-but-not-assignable entries (`core`, `libraries`, `payroll`, …). */
  get nonAssignableCount(): number {
    return this.catalog.length - this.assignableCount;
  }

  /** Entries that live inside another repo (`cast-stone` → `designer`). */
  get componentCount(): number {
    return this.catalog.filter((row) => !!row.componentOf).length;
  }

  /** The status values actually present, for the header's status select. */
  get statusOptions(): string[] {
    const seen = new Set<string>();
    for (const row of this.catalog) {
      const status = row.status?.trim();
      if (status) seen.add(status);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }

  /**
   * The newest `catalogUpdated` day-stamp across the mirrored rows — i.e. the
   * catalog document this HQ is mirroring. Null when nothing is governed yet.
   */
  get mirrorAsOf(): string | null {
    let newest: string | null = null;
    for (const row of this.catalog) {
      const stamp = row.catalogUpdated?.trim();
      if (stamp && (!newest || stamp > newest)) newest = stamp;
    }
    return newest;
  }

  /**
   * Whole days between {@link mirrorAsOf} and today. The sync warns (never
   * fails) past 30 days; the header surfaces the number so a stale mirror is
   * visible rather than inferred.
   */
  get mirrorStaleDays(): number | null {
    const asOf = dateValue(this.mirrorAsOf);
    if (asOf === null) return null;
    return Math.max(0, Math.floor((Date.now() - asOf) / 86_400_000));
  }

  /** True when a header facet or the search narrows the table. */
  get hasActiveFilters(): boolean {
    return (
      this.searchQuery.trim() !== "" ||
      this.kindFilter !== "all" ||
      this.statusFilter !== "all" ||
      this.assignableOnly
    );
  }

  /** Rows matching the header facets + search, before sorting. */
  get filteredModules(): ModuleCatalogRecord[] {
    let rows = this.catalog;

    if (this.kindFilter !== "all") {
      rows = rows.filter((row) => row.kind === this.kindFilter);
    }
    if (this.statusFilter !== "all") {
      rows = rows.filter((row) => (row.status ?? "") === this.statusFilter);
    }
    if (this.assignableOnly) {
      rows = rows.filter((row) => row.assignable !== false);
    }

    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) => this.matchesSearch(row, q));
    }
    return rows;
  }

  /** The rendered rows: {@link filteredModules} under the active column sort. */
  get sortedModules(): ModuleCatalogRecord[] {
    return sortRows(this.filteredModules, (row) => this.sortValue(row), this.sortDir);
  }

  /**
   * Search matches identity AND vocabulary: display name, catalog key, governed
   * repo, the owning repo of a component, plus the JSON-array columns — so
   * "which entry ships @intrasync/stores?" and "what used to be called X?" are
   * both answerable from the one box.
   */
  private matchesSearch(row: ModuleCatalogRecord, q: string): boolean {
    const haystack = [
      row.displayName,
      row.moduleKey,
      row.governedRepo,
      row.componentOf,
      row.category,
      ...parseJsonArrayColumn(row.aliases),
      ...parseJsonArrayColumn(row.legacyAliases),
      ...parseJsonArrayColumn(row.packages),
    ];
    return haystack.some((value) => !!value && value.toLowerCase().includes(q));
  }

  /**
   * Fold a governance rollup into ONE sortable number so the Governance column
   * orders by severity rather than by raw count: errors dominate warnings,
   * warnings dominate info. Null (sorts last) when there is no rollup.
   */
  private governanceWeight(summary?: ModuleSummaryRecord): number | null {
    const counts = summary?.governanceCounts;
    if (!counts) return null;
    return (counts.error ?? 0) * 1_000_000 + (counts.warn ?? 0) * 1_000 + (counts.info ?? 0);
  }

  /** Per-row cell value for the active sort key (nulls sort last). */
  private sortValue(row: ModuleCatalogRecord): SortValue {
    const summary = this.summaryOf(row);
    switch (this.sortKey) {
      case "key":
        return textValue(row.moduleKey);
      case "kind":
        return textValue(row.kind);
      case "status":
        return textValue(row.status);
      case "clients":
        return summary?.clientCount ?? null;
      case "owners":
        return summary?.ownerCount ?? null;
      case "governance":
        return this.governanceWeight(summary);
      case "activity":
        return dateValue(summary?.lastActivityAt);
      case "name":
      default:
        return textValue(row.displayName);
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    // No debounce and no requery: the whole catalog is already in memory.
    this.searchQuery = q;
  }

  setKindFilter(filter: ModuleKindFacet) {
    this.kindFilter = filter;
  }

  setStatusFilter(filter: ModuleStatusFilter) {
    this.statusFilter = filter;
  }

  toggleAssignableOnly() {
    this.assignableOnly = !this.assignableOnly;
  }

  /**
   * Column-header click: the same column toggles direction, a new column starts
   * in its natural direction (counts/dates desc, text asc). The ordering is
   * applied locally by {@link sortedModules} — no requery.
   */
  setSort(key: ModuleSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, MODULES_DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  private buildQuery(): ApiQueryParams {
    return {
      $limit: MODULES_FETCH_LIMIT,
      // A stable server ordering; the rendered order is the client-side sort.
      $sort: { displayName: 1 },
    };
  }

  /**
   * Fetch the whole catalog. No `assignable` predicate — this is the LIST
   * surface and it shows everything the Coordinator governs.
   */
  async loadCatalog() {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.error = null;
    try {
      // `findOrThrow`: the ServiceStore swallows a failed find (resolves null,
      // records errorOnFind), which used to render "No catalog entries match
      // the current filters" over cards of 0 while module-catalog was down.
      const result = await findOrThrow<PaginatedResult<ModuleCatalogRecord> | null>(
        this.catalogService,
        { query: this.buildQuery() },
      );
      if (seq !== this.loadSeq) return; // a newer load superseded this one
      const rows = rowsOf<ModuleCatalogRecord>(result);
      runInAction(() => {
        this.catalog = rows;
        this.total = typeof result?.total === "number" ? result.total : rows.length;
      });
      // The rollup rides along but never blocks (or fails) the table.
      void this.loadSummary();
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load the module catalog");
        // No rows under an error: a stale catalog must not read as current.
        this.catalog = [];
        this.total = 0;
      });

      console.error("Failed to load the module catalog:", err);
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Fetch the per-entry rollup in ONE find. Queried with an empty query: the
   * derived service returns a row per catalog entry and validates
   * additionalProperties:false with only an optional governedSlug, so `$limit`
   * would 400. Degrades on its own — a failure just hides the joined columns.
   * INTENTIONALLY TOLERANT (no page error): the rollup is decoration. It still
   * goes through `findOrThrow`, because a swallowed failure used to resolve
   * null and set `summaryAvailable = true` with every joined cell "-".
   */
  async loadSummary() {
    try {
      const result = await findOrThrow(this.summaryService, { query: {} });
      const map = new Map<string, ModuleSummaryRecord>();
      for (const row of rowsOf<ModuleSummaryRecord>(
        result as unknown as ModuleSummaryRecord[],
      )) {
        if (row?.governedSlug) map.set(row.governedSlug, row);
      }
      runInAction(() => {
        this.summaryBySlug = map;
        this.summaryAvailable = true;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.summaryAvailable = false;
      });

      console.warn("module-summary rollup unavailable:", err);
    }
  }

  /** Alias used by the header Refresh button / route onEnter. */
  async reload() {
    await this.loadCatalog();
  }

  // ---- Realtime -----------------------------------------------------------

  /**
   * Apply a live modules-area CRUD event.
   *
   * `module-catalog` events patch the row IN PLACE — no full reload — because
   * the catalog IS this list. Everything else (`module-owners`,
   * `module-governance`, `module-releases`) can only move the joined rollup, so
   * those re-pull the summary and leave the catalog alone. A catalog sync run
   * writes through these same services, so an open page follows the mirror.
   */
  handleLiveEvent(event: ModuleServiceEvent) {
    const { service, action, record } = event;
    if (service !== "module-catalog") {
      void this.loadSummary();
      return;
    }
    const row = record as unknown as ModuleCatalogRecord;
    if (!row?.id) return;
    runInAction(() => {
      const idx = this.catalog.findIndex((entry) => entry.id === row.id);
      if (action === "removed") {
        if (idx >= 0) {
          this.catalog.splice(idx, 1);
          this.total = Math.max(0, this.total - 1);
        }
        return;
      }
      if (action === "created") {
        if (idx < 0) {
          this.catalog = [...this.catalog, row];
          this.total += 1;
        }
        return;
      }
      // patched / updated
      if (idx >= 0) {
        const merged = { ...this.catalog[idx], ...row };
        if (merged.deletedAt) {
          // Retired out from under the view (soft delete).
          this.catalog.splice(idx, 1);
          this.total = Math.max(0, this.total - 1);
        } else {
          this.catalog[idx] = merged;
        }
      }
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.catalog = [];
    this.total = 0;
    this.loading = false;
    this.error = null;
    this.summaryBySlug = new Map<string, ModuleSummaryRecord>();
    this.summaryAvailable = false;
    this.searchQuery = "";
    this.kindFilter = "all";
    this.statusFilter = "all";
    this.assignableOnly = false;
    this.sortKey = "name";
    this.sortDir = "asc";
    this.unsubscribeLive = null;
    this.loadSeq += 1;
  }
}

export default ModulesStore;
